import { AIModule } from '../types';

export const EDGE_AI_OPTIMIZATION_MODULES: AIModule[] = [
  {
    id: 'edge-ai-optimization',
    title: 'Edge AI Optimization',
    description: 'Optimize AI models for edge devices with quantization, pruning, and hardware acceleration.',
    estimatedTime: '95 minutes',
    duration: '95 minutes',
    difficulty: 'intermediate' as const,
    content: `
# Edge AI Optimization

## Overview
Edge AI optimization focuses on making AI models efficient enough to run on resource-constrained devices while maintaining acceptable performance.

## Model Optimization Techniques

### 1. Quantization
Convert model weights from floating-point to lower precision.

\`\`\`python
import torch
import torch.quantization as quant

class QuantizedModel:
    def __init__(self, model):
        self.model = model
        
    def quantize_dynamic(self):
        """Dynamic quantization - weights quantized, activations computed in float"""
        quantized_model = torch.quantization.quantize_dynamic(
            self.model,
            {torch.nn.Linear, torch.nn.Conv2d},
            dtype=torch.qint8
        )
        return quantized_model
        
    def quantize_static(self, calibration_loader):
        """Static quantization - both weights and activations quantized"""
        # Prepare model for quantization
        self.model.qconfig = torch.quantization.get_default_qconfig('fbgemm')
        torch.quantization.prepare(self.model, inplace=True)
        
        # Calibrate with representative data
        self.model.eval()
        with torch.no_grad():
            for data, _ in calibration_loader:
                self.model(data)
                
        # Convert to quantized model
        quantized_model = torch.quantization.convert(self.model, inplace=False)
        return quantized_model
        
    def quantize_qat(self, train_loader, epochs=5):
        """Quantization Aware Training"""
        self.model.qconfig = torch.quantization.get_default_qat_qconfig('fbgemm')
        torch.quantization.prepare_qat(self.model, inplace=True)
        
        # Train with quantization simulation
        optimizer = torch.optim.SGD(self.model.parameters(), lr=0.001)
        criterion = torch.nn.CrossEntropyLoss()
        
        for epoch in range(epochs):
            for data, target in train_loader:
                optimizer.zero_grad()
                output = self.model(data)
                loss = criterion(output, target)
                loss.backward()
                optimizer.step()
                
        # Convert to quantized model
        self.model.eval()
        quantized_model = torch.quantization.convert(self.model, inplace=False)
        return quantized_model

# Example usage
model = torch.jit.load('model.pt')
quantizer = QuantizedModel(model)

# Dynamic quantization (easiest)
dynamic_quantized = quantizer.quantize_dynamic()

# Check model size reduction
original_size = sum(p.numel() * p.element_size() for p in model.parameters())
quantized_size = sum(p.numel() * p.element_size() for p in dynamic_quantized.parameters())
print(f"Size reduction: {original_size / quantized_size:.2f}x")
\`\`\`

### 2. Pruning
Remove unnecessary connections and neurons.

\`\`\`python
import torch.nn.utils.prune as prune

class PruningOptimizer:
    def __init__(self, model):
        self.model = model
        
    def structured_prune(self, amount=0.3):
        """Remove entire channels/filters"""
        for name, module in self.model.named_modules():
            if isinstance(module, torch.nn.Conv2d):
                prune.ln_structured(
                    module, 
                    name='weight', 
                    amount=amount, 
                    n=2, 
                    dim=0  # Prune output channels
                )
            elif isinstance(module, torch.nn.Linear):
                prune.ln_structured(
                    module,
                    name='weight',
                    amount=amount,
                    n=2,
                    dim=0  # Prune output features
                )
                
    def unstructured_prune(self, amount=0.5):
        """Remove individual weights"""
        parameters_to_prune = []
        for name, module in self.model.named_modules():
            if isinstance(module, (torch.nn.Conv2d, torch.nn.Linear)):
                parameters_to_prune.append((module, 'weight'))
                
        prune.global_unstructured(
            parameters_to_prune,
            pruning_method=prune.L1Unstructured,
            amount=amount,
        )
        
    def gradual_pruning(self, train_loader, initial_sparsity=0.0, final_sparsity=0.8, epochs=10):
        """Gradually increase pruning during training"""
        for epoch in range(epochs):
            # Calculate current sparsity
            progress = epoch / epochs
            current_sparsity = initial_sparsity + (final_sparsity - initial_sparsity) * progress
            
            # Apply pruning
            self.unstructured_prune(current_sparsity)
            
            # Train for one epoch
            self.train_epoch(train_loader)
            
    def calculate_sparsity(self):
        """Calculate model sparsity"""
        total_params = 0
        zero_params = 0
        
        for module in self.model.modules():
            if hasattr(module, 'weight'):
                total_params += module.weight.numel()
                zero_params += (module.weight == 0).sum().item()
                
        return zero_params / total_params
\`\`\`

### 3. Knowledge Distillation
Transfer knowledge from large teacher to small student model.

\`\`\`python
class KnowledgeDistillation:
    def __init__(self, teacher_model, student_model, temperature=4.0, alpha=0.7):
        self.teacher = teacher_model
        self.student = student_model
        self.temperature = temperature
        self.alpha = alpha  # Weight for distillation loss
        
    def distillation_loss(self, student_logits, teacher_logits, labels):
        """Compute distillation loss"""
        # Soft targets from teacher
        soft_targets = torch.nn.functional.softmax(teacher_logits / self.temperature, dim=1)
        soft_prob = torch.nn.functional.log_softmax(student_logits / self.temperature, dim=1)
        
        # Distillation loss
        distill_loss = torch.nn.functional.kl_div(soft_prob, soft_targets, reduction='batchmean')
        distill_loss *= (self.temperature ** 2)
        
        # Hard targets loss
        hard_loss = torch.nn.functional.cross_entropy(student_logits, labels)
        
        # Combined loss
        total_loss = self.alpha * distill_loss + (1 - self.alpha) * hard_loss
        return total_loss
        
    def train_student(self, train_loader, epochs=10):
        """Train student model with teacher guidance"""
        optimizer = torch.optim.Adam(self.student.parameters())
        
        self.teacher.eval()
        self.student.train()
        
        for epoch in range(epochs):
            total_loss = 0
            for batch_idx, (data, target) in enumerate(train_loader):
                optimizer.zero_grad()
                
                # Get teacher and student predictions
                with torch.no_grad():
                    teacher_logits = self.teacher(data)
                    
                student_logits = self.student(data)
                
                # Compute distillation loss
                loss = self.distillation_loss(student_logits, teacher_logits, target)
                loss.backward()
                optimizer.step()
                
                total_loss += loss.item()
                
            print(f"Epoch {epoch+1}/{epochs}, Loss: {total_loss/len(train_loader):.4f}")

# Usage example
teacher = LargeModel()  # Pre-trained large model
student = SmallModel()  # Smaller architecture
distiller = KnowledgeDistillation(teacher, student)
distiller.train_student(train_loader)
\`\`\`

## Hardware Acceleration

### 1. TensorRT Optimization (NVIDIA)

\`\`\`python
import tensorrt as trt
import pycuda.driver as cuda

class TensorRTOptimizer:
    def __init__(self):
        self.logger = trt.Logger(trt.Logger.WARNING)
        
    def onnx_to_tensorrt(self, onnx_path, precision='fp16'):
        """Convert ONNX model to TensorRT"""
        builder = trt.Builder(self.logger)
        network = builder.create_network()
        parser = trt.OnnxParser(network, self.logger)
        
        # Parse ONNX model
        with open(onnx_path, 'rb') as model:
            parser.parse(model.read())
            
        # Configure builder
        config = builder.create_builder_config()
        config.max_workspace_size = 1 << 30  # 1GB
        
        if precision == 'fp16':
            config.set_flag(trt.BuilderFlag.FP16)
        elif precision == 'int8':
            config.set_flag(trt.BuilderFlag.INT8)
            # Need calibration data for INT8
            
        # Build engine
        engine = builder.build_engine(network, config)
        return engine
        
    def benchmark_model(self, engine, input_shape, num_runs=100):
        """Benchmark TensorRT engine performance"""
        context = engine.create_execution_context()
        
        # Allocate device memory
        input_size = trt.volume(input_shape)
        output_size = trt.volume(engine.get_binding_shape(1))
        
        d_input = cuda.mem_alloc(input_size * 4)  # 4 bytes per float32
        d_output = cuda.mem_alloc(output_size * 4)
        
        stream = cuda.Stream()
        
        # Warm up
        for _ in range(10):
            context.execute_async_v2([d_input, d_output], stream.handle)
        stream.synchronize()
        
        # Benchmark
        start_time = time.time()
        for _ in range(num_runs):
            context.execute_async_v2([d_input, d_output], stream.handle)
        stream.synchronize()
        end_time = time.time()
        
        avg_time = (end_time - start_time) / num_runs * 1000  # ms
        return avg_time
\`\`\`

### 2. CoreML Optimization (Apple)

\`\`\`python
import coremltools as ct

def optimize_for_coreml(pytorch_model, example_input):
    """Convert and optimize PyTorch model for Apple devices"""
    
    # Trace the model
    traced_model = torch.jit.trace(pytorch_model, example_input)
    
    # Convert to CoreML
    coreml_model = ct.convert(
        traced_model,
        inputs=[ct.TensorType(shape=example_input.shape)],
        compute_precision=ct.precision.FLOAT16,  # Use FP16 for efficiency
        minimum_deployment_target=ct.target.iOS13
    )
    
    # Further optimizations
    spec = coreml_model.get_spec()
    
    # Enable neural engine optimization
    spec.neuralNetwork.arrayInputs[0].dataType = ct.proto.FeatureTypes_pb2.ArrayFeatureType.FLOAT16
    
    return coreml_model
\`\`\`

## Performance Monitoring

\`\`\`python
class EdgePerformanceMonitor:
    def __init__(self):
        self.metrics = {
            'inference_time': [],
            'memory_usage': [],
            'power_consumption': [],
            'accuracy': []
        }
        
    def profile_inference(self, model, data_loader, device='cpu'):
        """Profile model performance on edge device"""
        model.eval()
        
        with torch.no_grad():
            for batch_idx, (data, target) in enumerate(data_loader):
                data = data.to(device)
                
                # Measure inference time
                start_time = time.time()
                output = model(data)
                inference_time = time.time() - start_time
                
                # Measure memory usage
                if device == 'cuda':
                    memory_used = torch.cuda.memory_allocated() / 1024**2  # MB
                else:
                    import psutil
                    memory_used = psutil.Process().memory_info().rss / 1024**2
                    
                # Calculate accuracy
                pred = output.argmax(dim=1)
                accuracy = (pred == target).float().mean().item()
                
                self.metrics['inference_time'].append(inference_time)
                self.metrics['memory_usage'].append(memory_used)
                self.metrics['accuracy'].append(accuracy)
                
    def generate_report(self):
        """Generate performance report"""
        report = {
            'avg_inference_time': np.mean(self.metrics['inference_time']),
            'avg_memory_usage': np.mean(self.metrics['memory_usage']),
            'avg_accuracy': np.mean(self.metrics['accuracy']),
            'throughput': 1.0 / np.mean(self.metrics['inference_time'])  # samples/sec
        }
        return report
\`\`\`

## Best Practices

### Model Selection
- Choose appropriate architecture for target hardware
- Consider accuracy vs. efficiency trade-offs
- Use mobile-optimized architectures (MobileNet, EfficientNet)

### Optimization Pipeline
1. **Baseline measurement**: Profile original model
2. **Quantization**: Apply appropriate precision reduction
3. **Pruning**: Remove redundant parameters
4. **Distillation**: Transfer knowledge to smaller model
5. **Hardware optimization**: Use platform-specific optimizations
6. **Validation**: Ensure accuracy meets requirements

### Deployment Considerations
- **Batch size**: Often 1 for edge inference
- **Memory constraints**: Monitor peak memory usage
- **Power efficiency**: Balance performance with battery life
- **Update mechanisms**: Over-the-air model updates
`,
    learningObjectives: [
      'Apply quantization techniques to reduce model size',
      'Implement model pruning for efficiency',
      'Use knowledge distillation for model compression',
      'Optimize models for specific hardware platforms'
    ],
    quiz: {
      questions: [
        {
          id: 'edge-opt-1',
          question: 'What is the main difference between dynamic and static quantization?',
          options: [
            'Dynamic is faster than static',
            'Static quantizes activations, dynamic does not',
            'Dynamic requires calibration data',
            'Static only works on GPUs'
          ],
          correctAnswer: 1,
          explanation: 'Static quantization quantizes both weights and activations, while dynamic quantization only quantizes weights and computes activations in floating-point.'
        },
        {
          id: 'edge-opt-2',
          question: 'When should you use knowledge distillation?',
          options: [
            'When you need a larger model',
            'When you want to transfer knowledge from a large model to a small one',
            'Only for computer vision tasks',
            'When training from scratch'
          ],
          correctAnswer: 1,
          explanation: 'Knowledge distillation is used to transfer knowledge from a large, complex teacher model to a smaller, more efficient student model.'
        },
        {
          id: 'edge-opt-3',
          question: 'What is the advantage of structured pruning over unstructured pruning?',
          options: [
            'Better accuracy retention',
            'Hardware-friendly regular sparsity patterns',
            'Easier implementation',
            'No retraining required'
          ],
          correctAnswer: 1,
          explanation: 'Structured pruning creates regular sparsity patterns that are more hardware-friendly and can actually speed up inference on standard hardware.'
        },
        {
          id: 'edge-opt-4',
          question: 'Why is FP16 commonly used for edge AI optimization?',
          options: [
            'Better accuracy than FP32',
            'Reduces memory usage and can increase speed',
            'Required by all edge devices',
            'Easier to implement'
          ],
          correctAnswer: 1,
          explanation: 'FP16 reduces memory usage by half compared to FP32 and can increase inference speed on hardware that supports half-precision operations.'
        },
        {
          id: 'edge-opt-5',
          question: 'What should be monitored when deploying AI on edge devices?',
          options: [
            'Only accuracy metrics',
            'Inference time, memory usage, power consumption, and accuracy',
            'Only hardware utilization',
            'Training loss'
          ],
          correctAnswer: 1,
          explanation: 'Edge deployment requires monitoring multiple metrics including inference time, memory usage, power consumption, and accuracy to ensure optimal performance.'
        }
      ]
    }
  }
];